/**
 * Spec 004 P3: the QMC adapter behind echo's `quantApi` seam. Echo's copied hooks call the
 * `QuantApi` methods with echo's `/quant/*` shapes; this module answers them from QMC's `/api/*`
 * (the prefix lives in `apiFetch`), so the copied files stay byte-identical. Differences handled here:
 *
 *  - G1 `getConfig` is built from `GET /api/status` (`runs.max`); there is no Jupyter (`jupyter_url` is `''`).
 *  - G8 a log chunk `{text, offset, next_offset, done, more}` becomes `{data, offset: next_offset, live: !done, more}`.
 *  - G10 a null feed `symbols` becomes `[]` (the "unknown" signal is `live_instruments: null`, left as is).
 *  - G12 the bearer token is QMC's (`getToken()` inside `apiFetch`); a 401 raises the unlock event there.
 *
 * Every failure is an `ApiError`, so callers keep one error type.
 */
import { ApiError, apiFetch } from '@/app/lib/api'
import { quantError } from '@/app/lib/quant/types'

import { QUANT_ABORT_DETAIL, type ChartMessage, type DataCoverage, type FeedStatus, type InstrumentSources, type LiveEquity, type LogChunk, type RunDetail, type LiveEventsPage, type LiveSession, type LiveSessionsEnvelope, type StartLiveBody, type ParamValue, type PreviewRequestBody, type PreviewResponse, type QuantApi, type QuantConfig, type Run, type SavedConfig, type Strategy } from './types'

const TIMEOUT_STRATEGIES_MS = 30_000
const TIMEOUT_START_MS = 65_000
const TIMEOUT_CHART_MS = 60_000
const TIMEOUT_DELETE_MS = 40_000
/** A live start takes about 6 s (container boot and warm-up); 30 s is generous. */
export const TIMEOUT_LIVE_START_MS = 30_000
export const TIMEOUT_LIVE_EQUITY_MS = 20_000
export const TIMEOUT_LIVE_DELETE_MS = 30_000
/** A little above the 30 s preview limit of the backend (slowest observed answer: 6.1 s). */
export const PREVIEW_TIMEOUT_MS = 35_000
/** The feed route answers in about 0.2 s; a poll that takes 8 s has failed. */
export const FEED_TIMEOUT_MS = 8_000

const UNREACHABLE = 'Cannot reach the server.'
const NO_ANSWER = 'The server did not answer in time.'

interface CallInit {
  method?: string
  json?: unknown
}

async function call<T>(path: string, init: CallInit = {}, timeoutMs?: number, signal?: AbortSignal): Promise<T> {
  try {
    return await apiFetch<T>(path, { method: init.method, json: init.json, timeoutMs, signal })
  } catch (error) {
    if (error instanceof ApiError && error.status === 0) {
      throw quantError(0, error.code === 'timeout' ? NO_ANSWER : UNREACHABLE)
    }
    if (error instanceof ApiError) throw error
    if (signal?.aborted) throw quantError(0, QUANT_ABORT_DETAIL)
    if (error instanceof DOMException && error.name === 'AbortError') throw quantError(0, NO_ANSWER)
    throw quantError(0, UNREACHABLE)
  }
}

/** QMC's log chunk (`text`, `next_offset`, `done`, `more`) in echo's shape. */
interface WireLogChunk {
  text: string
  offset: number
  next_offset: number
  done: boolean
  more?: boolean
}

export function toLogChunk(wire: WireLogChunk): LogChunk {
  return { data: wire.text, offset: wire.next_offset, live: !wire.done, more: wire.more === true }
}

/** The status fields this adapter reads. */
interface WireStatus {
  runs: { max: number }
}

/** QMC's feed with a nullable `symbols`. */
type WireFeed = Omit<FeedStatus, 'symbols'> & { symbols: FeedStatus['symbols'] | null }

function runPath(id: string): string {
  return `/runs/${encodeURIComponent(id)}`
}

const configsPath = (slug: string): string => `/strategies/${encodeURIComponent(slug)}/configs`

const livePath = (id: string): string => `/live/${encodeURIComponent(id)}`

type RealMethods = Pick<
  QuantApi,
  'getConfig' | 'listStrategies' | 'listRuns' | 'getRun' | 'startRun' | 'cancelRun' | 'getRunLog' | 'getRunChart' | 'deleteRun' | 'getData' | 'getInstrumentSources' | 'getFeed'
  | 'listLiveSessions' | 'getLiveSession' | 'startLiveSession' | 'stopLiveSession'
  | 'getLiveEvents' | 'getLiveLog' | 'getLiveEquity' | 'deleteLiveSession'
  | 'listSavedConfigs' | 'saveConfig' | 'deleteConfig'
>

/** `postPreview` is not a `QuantApi` member: the REST preview session takes it as a dependency. */
export type QuantRealApi = RealMethods & {
  postPreview(slug: string, body: PreviewRequestBody, signal?: AbortSignal): Promise<PreviewResponse>
}

export const quantRealApi: QuantRealApi = {
  getConfig: async (): Promise<QuantConfig> => {
    const status = await call<WireStatus>('/status')
    return { jupyter_url: '', max_concurrent_runs: status.runs.max }
  },
  listStrategies: () =>
    call<{ contract_version: 1; strategies: Strategy[] }>('/strategies', {}, TIMEOUT_STRATEGIES_MS),
  listRuns: () => call<{ runs: Run[]; active: number; max: number; own_active?: number }>('/runs'),
  getRun: (id) => call<RunDetail>(runPath(id)),
  startRun: (body: { strategy: string; params: Record<string, ParamValue> }) =>
    call<RunDetail>('/runs', { method: 'POST', json: { strategy: body.strategy, params: body.params } }, TIMEOUT_START_MS),
  cancelRun: (id) => call<RunDetail>(`${runPath(id)}/cancel`, { method: 'POST' }),
  getRunLog: async (id, offset) => toLogChunk(await call<WireLogChunk>(`${runPath(id)}/log?offset=${offset}`)),
  getRunChart: (id, bars, signal) =>
    call<ChartMessage[]>(`${runPath(id)}/chart.json?bars=${bars}`, {}, TIMEOUT_CHART_MS, signal),
  deleteRun: async (id) => {
    await call<void>(runPath(id), { method: 'DELETE' }, TIMEOUT_DELETE_MS)
  },
  getData: (signal) => call<DataCoverage>('/data', {}, undefined, signal),
  getInstrumentSources: (signal) => call<InstrumentSources>('/instrument-sources', {}, undefined, signal),
  getFeed: async (signal): Promise<FeedStatus> => {
    const feed = await call<WireFeed>('/feed', {}, FEED_TIMEOUT_MS, signal)
    return { ...feed, symbols: feed.symbols ?? [] }
  },
  listLiveSessions: (signal) => call<LiveSessionsEnvelope>('/live', {}, undefined, signal),
  getLiveSession: (id, signal) => call<LiveSession>(livePath(id), {}, undefined, signal),
  startLiveSession: (body: StartLiveBody) =>
    call<LiveSession>(
      '/live',
      { method: 'POST', json: { strategy: body.strategy, params: body.params, account: body.account } },
      TIMEOUT_LIVE_START_MS,
    ),
  stopLiveSession: (id) => call<LiveSession>(`${livePath(id)}/stop`, { method: 'POST' }),
  getLiveEvents: (id, after, limit, signal) => {
    const query = new URLSearchParams()
    if (after !== null) query.append('after', String(after))
    query.append('limit', String(limit))
    return call<LiveEventsPage>(`${livePath(id)}/events?${query.toString()}`, {}, undefined, signal)
  },
  getLiveLog: async (id, offset, signal) =>
    toLogChunk(await call<WireLogChunk>(`${livePath(id)}/log?offset=${offset}`, {}, undefined, signal)),
  getLiveEquity: (id, maxPoints, signal) =>
    call<LiveEquity>(`${livePath(id)}/equity?max_points=${maxPoints}`, {}, TIMEOUT_LIVE_EQUITY_MS, signal),
  deleteLiveSession: async (id) => {
    await call<void>(livePath(id), { method: 'DELETE' }, TIMEOUT_LIVE_DELETE_MS)
  },
  listSavedConfigs: (slug, signal) => call<{ configs: SavedConfig[] }>(configsPath(slug), {}, undefined, signal),
  saveConfig: (slug, name, params) =>
    call<SavedConfig>(`${configsPath(slug)}/${encodeURIComponent(name)}`, { method: 'PUT', json: { params } }),
  deleteConfig: async (slug, name) => {
    await call<void>(`${configsPath(slug)}/${encodeURIComponent(name)}`, { method: 'DELETE' })
  },
  postPreview: (slug, body, signal) =>
    call<PreviewResponse>(
      `/strategies/${encodeURIComponent(slug)}/preview`,
      { method: 'POST', json: body },
      PREVIEW_TIMEOUT_MS,
      signal,
    ),
}
